import React, { useEffect, useState } from 'react';
import { Puzzle, Coins } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getModuleRegistry, setCompanyModules } from '@/lib/auth';

let registryCache = null;
export const useModuleRegistry = () => {
  const [reg, setReg] = useState(registryCache);
  useEffect(() => {
    if (registryCache) return;
    getModuleRegistry().then((r) => { registryCache = r; setReg(r); }).catch(() => toast.error('Could not load the module list'));
  }, []);
  return reg;
};

const CostChips = ({ costs, labels }) => (costs || []).length === 0 ? (
  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800">No running cost</span>
) : (
  <>
    {costs.map((c) => (
      <span key={c} className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-900 border border-amber-200">
        <Coins className="w-3 h-3" />{labels?.[c] || c}
      </span>
    ))}
  </>
);

// Template picker used when creating a company
export const TemplatePicker = ({ value, onChange }) => {
  const reg = useModuleRegistry();
  if (!reg) return <p className="text-xs text-[#5C6670]">Loading…</p>;
  const names = Object.fromEntries(reg.modules.map((m) => [m.key, m.name]));
  return (
    <div className="space-y-2">
      {reg.templates.map((t) => (
        <button type="button" key={t.key} onClick={() => onChange(t.key)}
          className={`w-full text-left rounded-lg border p-3 ${value === t.key ? 'border-[#1E2A33] bg-[#FBF8F2]' : 'border-[#E3DDD1]'}`}>
          <div className="text-sm font-semibold">{t.name}</div>
          <div className="text-xs text-[#5C6670]">{t.description}</div>
          <div className="text-[11px] text-[#5C6670] mt-1">{t.modules.length ? `Core + ${t.modules.map((k) => names[k]).join(', ')}` : 'Core only'}</div>
        </button>
      ))}
    </div>
  );
};

// Per-company modules, on the company's page in Companies. Only super admins see this.
export default function ModulesCard({ companyId, data, onSaved }) {
  const reg = useModuleRegistry();
  const [mods, setMods] = useState(data.modules || []);
  const [limit, setLimit] = useState(data.limits?.newsletter_monthly ?? 1000);
  const [saving, setSaving] = useState(null);
  useEffect(() => { setMods(data.modules || []); setLimit(data.limits?.newsletter_monthly ?? 1000); }, [data]);
  if (!reg) return null;

  const byKey = Object.fromEntries(reg.modules.map((m) => [m.key, m]));
  const save = async (body, label) => {
    setSaving(label);
    try {
      const r = await setCompanyModules(companyId, body);
      setMods(r.modules); setLimit(r.limits.newsletter_monthly);
      onSaved && onSaved(r);
      toast.success(label, { description: 'Their team sees the change next time they load Stagehead.' });
    } catch (e) { toast.error('Could not save', { description: e.message }); } finally { setSaving(null); }
  };
  const toggle = (key, on) => {
    let next = on ? [...mods, key] : mods.filter((k) => k !== key);
    if (!on) next = next.filter((k) => !(byKey[k].requires || []).includes(key)); // switching off what others need
    const m = byKey[key];
    if (on && m.costs.length && !window.confirm(`${m.name} costs money to run (${m.costs.map((c) => reg.cost_labels[c]).join(', ')}). Switch it on for this company?`)) return;
    save({ modules: next }, `${m.name} ${on ? 'switched on' : 'switched off'}`);
  };
  const groups = [...new Set(reg.modules.map((m) => m.group))];
  const costly = mods.filter((k) => byKey[k]?.costs.length);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-3 flex-wrap">
          <Puzzle className="w-5 h-5 text-[#A62B1F] mt-0.5" />
          <div className="flex-1 min-w-[200px]">
            <CardTitle>Modules</CardTitle>
            <CardDescription>
              Always on: {reg.core.join(', ')}. Everything else is off until you switch it on here. Companies can't switch modules on themselves.
            </CardDescription>
            {costly.length > 0 && <p className="text-xs text-amber-900 mt-1">{costly.length} module{costly.length === 1 ? '' : 's'} with running costs: {costly.map((k) => byKey[k].name).join(', ')}.</p>}
          </div>
          <select className="h-9 rounded-md border border-input px-2 text-sm bg-white" value="" disabled={!!saving}
            onChange={(e) => {
              const t = reg.templates.find((x) => x.key === e.target.value);
              if (t && window.confirm(`Replace this company's modules with the “${t.name}” set? Anything not in it is switched off.`)) save({ template: t.key }, `${t.name} modules applied`);
            }}>
            <option value="">Apply a template…</option>
            {reg.templates.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
          </select>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {groups.map((g) => (
          <div key={g}>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[#5C6670] mb-1">{g}</div>
            <div className="divide-y divide-[#E3DDD1]">
              {reg.modules.filter((m) => m.group === g).map((m) => {
                const on = mods.includes(m.key);
                const missing = (m.requires || []).filter((r) => !mods.includes(r));
                return (
                  <div key={m.key} className="py-2.5">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium">{m.name}</span>
                          <CostChips costs={m.costs} labels={reg.cost_labels} />
                        </div>
                        <p className="text-xs text-[#5C6670]">{m.description}{missing.length > 0 && !on ? ` Also switches on ${missing.map((r) => byKey[r].name).join(', ')}.` : ''}</p>
                      </div>
                      <Switch checked={on} disabled={!!saving} onCheckedChange={(v) => toggle(m.key, v)} />
                    </div>
                    {m.key === 'bulletin' && on && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap text-xs text-[#5C6670]">
                        Newsletter emails allowed per month
                        <Input type="number" min={0} className="h-8 w-28" value={limit} onChange={(e) => setLimit(e.target.value)} />
                        <Button size="sm" variant="outline" disabled={!!saving || Number(limit) === (data.limits?.newsletter_monthly ?? 1000)}
                          onClick={() => save({ limits: { newsletter_monthly: Number(limit) } }, 'Newsletter allowance saved')}>Save</Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
